import { useEffect, useMemo, useState } from "react";
import { BodyMetricType } from "../../../enums";
import { useApiQuery } from "../../../infrastructure/hooks/useApiQuery";
import { IBodyMetricEntry, IBodyMetricsDataSource } from "../models/bodyMetricModels";
import { summarizeBodyMetrics } from "../utils/bodyMetricsUtility";

/**
 * Owns the metric list query and which dialog is open (the "+" picker, or one metric's
 * details) for the reusable BodyMetricsOverview, so the same view serves "Моите метрики"
 * and the staff page on a user's profile.
 */
export const useBodyMetricsOverview = (dataSource: IBodyMetricsDataSource) => {
  const { data: entries, isLoading } = useApiQuery<IBodyMetricEntry[]>({
    queryKey: dataSource.listQueryKey,
    endpoint: dataSource.listEndpoint,
    globalIsLoading: false,
  });

  const summaries = useMemo(() => summarizeBodyMetrics(entries ?? []), [entries]);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addPresetType, setAddPresetType] = useState<BodyMetricType | null>(null);
  const [openType, setOpenType] = useState<BodyMetricType | null>(null);

  // The details close by themselves once the last reading of that metric is deleted.
  const openSummary = summaries.find((s) => s.type === openType) ?? null;

  // ...and forget that metric, so a later first reading of it doesn't pop its details open.
  useEffect(() => {
    if (openType !== null && entries && !openSummary) {
      setOpenType(null);
    }
  }, [openType, entries, openSummary]);

  const openAdd = (type: BodyMetricType | null = null) => {
    setAddPresetType(type);
    setIsAddOpen(true);
  };

  return {
    summaries,
    isLoading,
    hasAny: summaries.length > 0,
    isAddOpen,
    addPresetType,
    openAdd,
    closeAdd: () => setIsAddOpen(false),
    openSummary,
    openDetails: setOpenType,
    closeDetails: () => setOpenType(null),
  };
};
